import { KEYPOINT_COUNT, STRIDE } from '../pose/halpe26.ts';
import type { FrameState } from '../pose/frameState.ts';

/**
 * TELEFON ↔ TV MESAJ SÖZLEŞMESİ
 * ===========================================================================
 * Tek kaynak. Telefon tarafındaki gönderici de, TV'deki alıcı da bu dosyayı
 * import eder — biri değişip diğeri değişmesin diye.
 *
 * ⚠️ Bu klasörde göreli importlar `.ts` uzantılı olmalı, `@/` kullanılmaz:
 * modül hem React Native tarafında Metro ile, hem alıcı paketlenirken
 * esbuild ile, hem de Node'da çalışabilmeli.
 *
 * TASARIM NOTLARI
 * ---------------------------------------------------------------------------
 * • Poz mesajı saniyede 12-15 kez gidiyor, DAHA SIK DEĞİL. Cast'in özel mesaj
 *   kanalı yüksek frekanslı bir taşıma katmanı değil; 30 Hz tasarlarsanız
 *   mesajlar birikir. Alıcı iki poz arasını yumuşatarak farkı kapatıyor.
 * • Koordinatlar 0..1000 tam sayıya yuvarlanıyor. Ondalıklı JSON mesajı üç
 *   katına çıkarıyor ve piksel altı hassasiyetin TV mesafesinde karşılığı yok.
 * • Skor ve kadraj mesajları OLAY BAZLI — her karede değil, yalnızca değişince.
 */

/** Sözleşme sürümü. Kırıcı bir değişiklikte artırın; alıcı uyumsuzsa uyarır. */
export const TV_PROTOCOL_VERSION = 1;

/**
 * Cast özel mesaj kanalının adı.
 *
 * Gönderici ve alıcı BİREBİR aynı dizgiyi kullanmak zorunda. Uyuşmazlıkta
 * hiçbir hata çıkmaz, mesajlar sessizce düşer — o yüzden iki tarafta ayrı
 * sabit tutmak yerine burada tek kaynakta duruyor.
 */
export const CAST_NAMESPACE = 'urn:x-cast:com.danceweightloss.tv';

/** Koordinatların ölçeklendiği tam sayı aralığı. */
export const COORD_SCALE = 1000;
/** Görünürlüğün ölçeklendiği tam sayı aralığı. */
export const VIS_SCALE = 9;

/** Poz mesajındaki keypoint başına sayı adedi: x, y, görünürlük. */
export const PACKED_STRIDE = 3;

/** İskelet karesi. En sık giden mesaj, o yüzden alan adları tek harf. */
export type PoseMessage = {
  t: 'pose';
  /** Gönderenin saatinde ms. */
  ts: number;
  /** `[x0, y0, v0, x1, y1, v1, ...]` — x/y 0..1000, v 0..9. */
  p: number[];
};

/** Kadraj durumu değiştiğinde. */
export type FrameMessage = {
  t: 'frame';
  state: FrameState;
};

/** Skor değiştiğinde. */
export type ScoreMessage = {
  t: 'score';
  total: number;
  combo: number;
  /** Son hareketin değerlendirmesi — TV'de kısa bir baloncuk olarak çıkar. */
  rating?: 'perfect' | 'good' | 'miss';
};

/** Saat hizalaması: telefon sorar. */
export type PingMessage = { t: 'ping'; ts: number };

/**
 * Saat hizalaması: alıcı cevaplar.
 *
 * `mediaTime`, cevabın yazıldığı andaki video konumu (saniye). Telefon gidiş
 * dönüşün yarısını ekleyerek "şu an videonun neresindeyiz" sorusunu
 * kestiriyor. Son ölçümlerin MEDYANINI alın, ortalamasını değil — tek bir ağ
 * tıkanması ortalamayı bozar.
 */
export type PongMessage = { t: 'pong'; ts: number; mediaTime: number };

/** Alıcının açılışta bildirdiği sürüm. */
export type HelloMessage = { t: 'hello'; version: number };

export type TvMessage =
  | PoseMessage
  | FrameMessage
  | ScoreMessage
  | PingMessage
  | PongMessage
  | HelloMessage;

const clamp = (value: number, min: number, max: number): number =>
  value < min ? min : value > max ? max : value;

/**
 * Dik uzaydaki düz keypoint dizisini (0..1 ondalık) mesaj biçimine sıkıştırır.
 * Girdi `src/domain/pose` sözleşmesindeki 26 x 3 dizidir.
 */
export const packKeypoints = (keypoints: readonly number[]): number[] => {
  const out = new Array<number>(KEYPOINT_COUNT * PACKED_STRIDE);
  for (let k = 0; k < KEYPOINT_COUNT; k += 1) {
    const i = k * STRIDE;
    const o = k * PACKED_STRIDE;
    out[o] = Math.round(clamp(keypoints[i] ?? 0, 0, 1) * COORD_SCALE);
    out[o + 1] = Math.round(clamp(keypoints[i + 1] ?? 0, 0, 1) * COORD_SCALE);
    out[o + 2] = Math.round(clamp(keypoints[i + 2] ?? 0, 0, 1) * VIS_SCALE);
  }
  return out;
};

/** Sıkıştırılmış diziyi 0..1 ondalıklı hâline çevirir (alıcı tarafı). */
export const unpackKeypoints = (packed: readonly number[]): number[] => {
  const out = new Array<number>(KEYPOINT_COUNT * STRIDE);
  for (let k = 0; k < KEYPOINT_COUNT; k += 1) {
    const i = k * PACKED_STRIDE;
    const o = k * STRIDE;
    out[o] = (packed[i] ?? 0) / COORD_SCALE;
    out[o + 1] = (packed[i + 1] ?? 0) / COORD_SCALE;
    out[o + 2] = (packed[i + 2] ?? 0) / VIS_SCALE;
  }
  return out;
};

/** Gelen ham veri gerçekten bizim mesajımız mı. Alıcı tarafında zorunlu. */
export const isTvMessage = (value: unknown): value is TvMessage => {
  if (typeof value !== 'object' || value === null) return false;
  const t = (value as { t?: unknown }).t;
  return (
    t === 'pose' ||
    t === 'frame' ||
    t === 'score' ||
    t === 'ping' ||
    t === 'pong' ||
    t === 'hello'
  );
};
